import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Form, Input, Button, Card, Select, DatePicker, Switch, message, Spin, Popconfirm, Space, Alert, Radio, Upload } from 'antd';
import { DeleteOutlined, PlayCircleOutlined, StopOutlined, LoadingOutlined, CopyOutlined, UploadOutlined, LeftOutlined, RightOutlined, PlaySquareOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { streamService } from '../../services/streamService';
import { Stream, StreamRequest } from '../../types/stream';
import { apiRequest } from '../../services/api';
import { categoryService } from '../../services/categoryService';
import { deliveryService } from '../../services/deliveryService';
import { advSetService } from '../../services/advSetService';
import { logoService } from '../../services/logoService';
import { AdvSet } from '../../types/adv';
import { Logo } from '../../types/logo';

// Lazy load the VideoPlayer component
const VideoPlayer = lazy(() => import('./VideoPlayer'));
// Lazy load the OutgoingStreamPlayerContainer component
const OutgoingStreamPlayerContainer = lazy(() => import('./OutgoingStreamPlayerContainer'));

// Configure dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

// VideoPlayerContainer component to connect server data with player
interface VideoPlayerContainerProps {
  streamData: Stream;
  servers: Server[];
}

const VideoPlayerContainer: React.FC<VideoPlayerContainerProps> = ({ streamData, servers }) => {
  const { t } = useTranslation();
  const [serverUrl, setServerUrl] = useState<string>('');
  
  useEffect(() => {
    // Find the server in the servers array
    const server = servers.find(s => s.server_id === streamData.server_id);
    if (server) {
      setServerUrl(server.server_url || '');
    }
  }, [streamData.server_id, servers]);

  if (!serverUrl) {
    return <Alert message={t('streams.player.server_not_found', 'Server information not found')} type="warning" />;
  }

  if (!streamData.transcoder_application_name || !streamData.transcoder_stream_name) {
    return <Alert message={t('streams.player.missing_stream_info', 'Stream information is incomplete')} type="warning" />;
  }

  return (
    <VideoPlayer 
      serverUrl={serverUrl}
      transcoderApplicationName={streamData.transcoder_application_name}
      transcoderStreamName={streamData.transcoder_stream_name}
      enableQualitySelector={false}
    />
  );
};

const { TextArea } = Input;
const { Option } = Select;

interface StreamFormProps {
  isEdit?: boolean;
}

interface IncomingStream {
  id: string;
  name: string;
  server_id: string;
}

interface IncomingStreamsResponse {
  streams: IncomingStream[];
}

interface Server {
  id: number;
  server_id: string;
  server_name: string;
  server_url: string;
}

const StreamForm: React.FC<StreamFormProps> = ({ isEdit = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [formLocked, setFormLocked] = useState<boolean>(false);
  const [categories, setCategories] = useState<any[]>([]);
  const [servers, setServers] = useState<Server[]>([]);
  const [deliveries, setDeliveries] = useState<any[]>([]);
  const [incomingStreams, setIncomingStreams] = useState<IncomingStream[]>([]);
  const [loadingIncomingStreams, setLoadingIncomingStreams] = useState<boolean>(false);
  const [streamData, setStreamData] = useState<Stream | null>(null);
  const [processingStream, setProcessingStream] = useState<boolean>(false);
  const [activePlayer, setActivePlayer] = useState<'outgoing' | 'incoming' | 'origin'>('outgoing');
  const [uploadingHorizontal, setUploadingHorizontal] = useState<boolean>(false);
  const [uploadingVertical, setUploadingVertical] = useState<boolean>(false);
  const [horizontalImageDimensions, setHorizontalImageDimensions] = useState<{width: number, height: number} | null>(null);
  const [verticalImageDimensions, setVerticalImageDimensions] = useState<{width: number, height: number} | null>(null);
  const [horizontalImageKey, setHorizontalImageKey] = useState<number>(Date.now());
  const [verticalImageKey, setVerticalImageKey] = useState<number>(Date.now());
  const [advSets, setAdvSets] = useState<AdvSet[]>([]);
  const [triggeringAd, setTriggeringAd] = useState<boolean>(false);
  const [adCooldown, setAdCooldown] = useState<number>(0);
  const [blockedAdvSets, setBlockedAdvSets] = useState<number[]>([]);
  const [togglingAdvSet, setTogglingAdvSet] = useState<number | null>(null);
  const [startNowLockTimer, setStartNowLockTimer] = useState<number>(0);
  const [logos, setLogos] = useState<Logo[]>([]);
  const [updatingLogo, setUpdatingLogo] = useState<boolean>(false);

  // Copy to clipboard function
  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success(t('streams.notifications.copied_to_clipboard', `${label} copied to clipboard`));
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      message.error(t('streams.notifications.copy_failed', 'Failed to copy to clipboard'));
    }
  };

  // Open stream URL in new window with player domain
  const openStreamInPlayer = (streamUrl: string) => {
    if (!streamUrl) return;
    
    const playerDomain = process.env.REACT_APP_PLAYER_DOMAIN || '';
    if (!playerDomain) {
      message.error(t('streams.notifications.player_domain_not_configured', 'Player domain is not configured'));
      return;
    }
    
    const fullUrl = `${playerDomain}${streamUrl}`;
    window.open(fullUrl, '_blank', 'noopener,noreferrer');
  };

  // Fetch active advertisement sets
  const fetchAdvSets = async () => {
    try {
      const allAdvSets = await advSetService.getAllAdvSets();
      const activeAdvSets = allAdvSets.filter(advSet => advSet.adv_set_active);
      setAdvSets(activeAdvSets);
    } catch (error) {
      console.error('Error fetching advertisement sets:', error);
    }
  };

  // Fetch all logos
  const fetchLogos = async () => {
    try {
      const allLogos = await logoService.getAllLogos();
      setLogos(allLogos);
    } catch (error) {
      console.error('Error fetching logos:', error);
    }
  };

  // Fetch blocked advertisement sets for current stream
  const fetchBlockedAdvSets = async () => {
    if (!id) return;
    
    try {
      const response = await apiRequest(`/streams/${id}/block_adv_sets`) as Array<{adv_set_id: number}>;
      const blockedIds = response.map((blocked) => blocked.adv_set_id);
      setBlockedAdvSets(blockedIds);
    } catch (error) {
      console.error('Error fetching blocked advertisement sets:', error);
    }
  };

  // Toggle advertisement set blocking status
  const handleToggleAdvSet = async (advSetId: number) => {
    if (!id || togglingAdvSet) return;
    
    // Start 15-second lock timer for "Start now" button if no active timer exists
    if (startNowLockTimer === 0 && adCooldown === 0) {
      setStartNowLockTimer(15);
      const lockInterval = setInterval(() => {
        setStartNowLockTimer(prev => {
          if (prev <= 1) {
            clearInterval(lockInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    setTogglingAdvSet(advSetId);
    try {
      await apiRequest(`/streams/${id}/block_adv_sets`, {
        method: 'POST',
        body: JSON.stringify({
          adv_set_id: advSetId
        })
      });
      
      // Update local state
      setBlockedAdvSets(prev => 
        prev.includes(advSetId) 
          ? prev.filter(id => id !== advSetId)
          : [...prev, advSetId]
      );
      
      const isNowBlocked = !blockedAdvSets.includes(advSetId);
      message.success(
        isNowBlocked 
          ? t('streams.notifications.adv_set_blocked', 'Advertisement set blocked')
          : t('streams.notifications.adv_set_unblocked', 'Advertisement set unblocked')
      );
      
    } catch (error) {
      console.error('Error toggling advertisement set:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to toggle advertisement set';
      message.error(t('streams.notifications.error', { message: errorMessage }));
    } finally {
      setTogglingAdvSet(null);
    }
  };

  // Handle logo assignment to stream
  const handleLogoChange = async (logoId: number | null) => {
    if (!id || updatingLogo || !streamData) return;

    setUpdatingLogo(true);
    try {
      // Use existing stream data and only update logo_id
      const updateData: StreamRequest = {
        title: streamData.title,
        server_id: streamData.server_id,
        delivery_id: streamData.delivery_id,
        category_id: streamData.category_id || undefined,
        logo_id: logoId || undefined,
        server_incoming_stream_id: streamData.server_incoming_stream_id || undefined,
        server_transcoder_id: streamData.server_transcoder_id,
        description: streamData.description || undefined,
        image: streamData.image || undefined,
        image_horizontal: streamData.image_horizontal || undefined,
        image_vertical: streamData.image_vertical || undefined,
        start_datetime: streamData.start_datetime,
        end_datetime: streamData.end_datetime,
        end_datetime_flexible: streamData.end_datetime_flexible,
        status: streamData.status,
        is_planned: streamData.is_planned,
        is_live: streamData.is_live,
        is_ended: streamData.is_ended,
        published: streamData.published,
      };

      await streamService.updateStream(parseInt(id), updateData);

      // Update local stream data
      setStreamData({
        ...streamData,
        logo_id: logoId
      });

      message.success(
        logoId 
          ? t('streams.notifications.logo_assigned', 'Logo assigned to stream')
          : t('streams.notifications.logo_removed', 'Logo removed from stream')
      );

    } catch (error) {
      console.error('Error updating stream logo:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update stream logo';
      message.error(t('streams.notifications.error', { message: errorMessage }));
    } finally {
      setUpdatingLogo(false);
    }
  };

  // Trigger advertisement
  const handleTriggerAd = async () => {
    if (!id || triggeringAd || adCooldown > 0) return;
    
    setTriggeringAd(true);
    try {
      const response = await apiRequest(`/streams/${id}/adv`, {
        method: 'POST'
      });
      
      message.success(t('streams.notifications.ad_triggered', 'Advertisement triggered successfully'));
      
      // Start 120-second cooldown
      setAdCooldown(120);
      const cooldownInterval = setInterval(() => {
        setAdCooldown(prev => {
          if (prev <= 1) {
            clearInterval(cooldownInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
    } catch (error) {
      console.error('Error triggering advertisement:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to trigger advertisement';
      message.error(t('streams.notifications.error', { message: errorMessage }));
    } finally {
      setTriggeringAd(false);
    }
  };

  // Handle image upload
  const handleImageUpload = async (file: File, imageType: 'horizontal' | 'vertical') => {
    if (!id && !isEdit) {
      message.error(t('streams.form.save_before_upload', 'Please save the stream before uploading images'));
      return false;
    }

    const streamId = id || streamData?.id;
    if (!streamId) {
      message.error(t('streams.form.stream_id_required', 'Stream ID is required for image upload'));
      return false;
    }

    if (imageType === 'horizontal') {
      setUploadingHorizontal(true);
    } else {
      setUploadingVertical(true);
    }

    try {
      const formData = new FormData();
      formData.append('image', file);

      // Get auth token from localStorage
      const storedSession = localStorage.getItem('userSession');
      const headers: Record<string, string> = {};
      
      if (storedSession) {
        const session = JSON.parse(storedSession);
        headers['Authorization'] = `Bearer ${session.token}`;
      }

      const response = await fetch(`/api/streams/${streamId}/upload/${imageType}`, {
        method: 'POST',
        headers,
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const result = await response.json();
      message.success(t('streams.form.upload_success', `${imageType} image uploaded successfully`));
      
      // Refresh stream data to show the new image
      if (isEdit && id) {
        const refreshedStreamData = await streamService.getStreamById(Number(id));
        setStreamData(refreshedStreamData);
        
        // Update cache key to force image refresh
        if (imageType === 'horizontal') {
          setHorizontalImageKey(Date.now());
        } else {
          setVerticalImageKey(Date.now());
        }
      }
      
      return true;
    } catch (error) {
      console.error('Upload error:', error);
      message.error(t('streams.form.upload_error', 'Failed to upload image'));
      return false;
    } finally {
      if (imageType === 'horizontal') {
        setUploadingHorizontal(false);
      } else {
        setUploadingVertical(false);
      }
    }
  };

  // Handle image deletion
  const handleImageDelete = async (imageType: 'horizontal' | 'vertical') => {
    if (!id && !isEdit) {
      message.error(t('streams.form.save_before_delete', 'Please save the stream before deleting images'));
      return false;
    }

    const streamId = id || streamData?.id;
    if (!streamId) {
      message.error(t('streams.form.stream_id_required', 'Stream ID is required for image deletion'));
      return false;
    }

    if (imageType === 'horizontal') {
      setUploadingHorizontal(true);
    } else {
      setUploadingVertical(true);
    }

    try {
      // Get auth token from localStorage
      const storedSession = localStorage.getItem('userSession');
      const headers: Record<string, string> = {};
      
      if (storedSession) {
        const session = JSON.parse(storedSession);
        headers['Authorization'] = `Bearer ${session.token}`;
      }

      const response = await fetch(`/api/streams/${streamId}/delete/${imageType}`, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        throw new Error('Delete failed');
      }

      const result = await response.json();
      message.success(t('streams.form.delete_success', `${imageType} image deleted successfully`));
      
      // Refresh stream data to remove the image
      if (isEdit && id) {
        const refreshedStreamData = await streamService.getStreamById(Number(id));
        setStreamData(refreshedStreamData);
        
        // Update cache key to force image refresh
        if (imageType === 'horizontal') {
          setHorizontalImageKey(Date.now());
        } else {
          setVerticalImageKey(Date.now());
        }
      }
      
      return true;
    } catch (error) {
      console.error('Delete error:', error);
      message.error(t('streams.form.delete_error', 'Failed to delete image'));
      return false;
    } finally {
      if (imageType === 'horizontal') {
        setUploadingHorizontal(false);
      } else {
        setUploadingVertical(false);
      }
    }
  };

  const handleImagePreviewClick = (imageUrl: string) => {
    window.open(imageUrl, '_blank', 'noopener,noreferrer');
  };

  const handleImageLoad = (imageType: 'horizontal' | 'vertical', event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.target as HTMLImageElement;
    const dimensions = { width: img.naturalWidth, height: img.naturalHeight };
    
    if (imageType === 'horizontal') {
      setHorizontalImageDimensions(dimensions);
    } else {
      setVerticalImageDimensions(dimensions);
    }
  };

  // Generate stream URLs for different qualities using delivery domain (CDN)
  const generateStreamUrls = () => {
    if (!streamData || !streamData.transcoder_stream_name || !streamData.transcoder_application_name) {
      return {
        auto: '',
        '1080': '',
        '720': '',
        '480': '',
        '360': ''
      };
    }

    const selectedDelivery = deliveries.find(d => d.id === streamData.delivery_id);
    if (!selectedDelivery) {
      return {
        auto: '',
        '1080': '',
        '720': '',
        '480': '',
        '360': ''
      };
    }

    const baseUrl = selectedDelivery.domain;
    const applicationName = streamData.transcoder_application_name;
    const streamName = streamData.transcoder_stream_name;
    
    return {
      auto: `${baseUrl}/${applicationName}/${streamName}/playlist.m3u8`,
      '1080': `${baseUrl}/${applicationName}/${streamName}_4000/playlist.m3u8`,
      '720': `${baseUrl}/${applicationName}/${streamName}_2000/playlist.m3u8`,
      '480': `${baseUrl}/${applicationName}/${streamName}_1000/playlist.m3u8`,
      '360': `${baseUrl}/${applicationName}/${streamName}_500/playlist.m3u8`
    };
  };

  // Generate stream URLs for different qualities using server URL (Origin)
  const generateOriginStreamUrls = () => {
    if (!streamData || !streamData.transcoder_stream_name || !streamData.transcoder_application_name || !streamData.server_id) {
      return {
        auto: '',
        '1080': '',
        '720': '',
        '480': '',
        '360': ''
      };
    }

    const selectedServer = servers.find(s => s.server_id === streamData.server_id);
    if (!selectedServer || !selectedServer.server_url) {
      return {
        auto: '',
        '1080': '',
        '720': '',
        '480': '',
        '360': ''
      };
    }

    const baseUrl = selectedServer.server_url;
    const applicationName = streamData.transcoder_application_name;
    const streamName = streamData.transcoder_stream_name;
    
    return {
      auto: `${baseUrl}/${applicationName}/${streamName}/playlist.m3u8`,
      '1080': `${baseUrl}/${applicationName}/${streamName}_4000/playlist.m3u8`,
      '720': `${baseUrl}/${applicationName}/${streamName}_2000/playlist.m3u8`,
      '480': `${baseUrl}/${applicationName}/${streamName}_1000/playlist.m3u8`,
      '360': `${baseUrl}/${applicationName}/${streamName}_500/playlist.m3u8`
    };
  };

  // Generate incoming stream URL that is used by the player
  const generateIncomingStreamUrl = () => {
    if (!streamData || !streamData.server_incoming_stream_app || !streamData.server_incoming_stream_stream || !streamData.server_id) {
      return '';
    }

    const selectedServer = servers.find(s => s.server_id === streamData.server_id);
    if (!selectedServer || !selectedServer.server_url) {
      return '';
    }

    const baseUrl = selectedServer.server_url;
    const applicationName = streamData.server_incoming_stream_app;
    const streamName = streamData.server_incoming_stream_stream;
    
    return `${baseUrl}/${applicationName}/${streamName}/playlist.m3u8`;
  };

  // Fetch incoming streams
  const fetchIncomingStreams = async () => {
    setLoadingIncomingStreams(true);
    try {
      const response = await apiRequest<IncomingStreamsResponse>('/servers/incoming');
      setIncomingStreams(response.streams || []);
    } catch (error) {
      console.error('Error fetching incoming streams:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load incoming streams';
      message.error(t('streams.notifications.error', { message: errorMessage }));
    } finally {
      setLoadingIncomingStreams(false);
    }
  };

  // Fetch servers
  const fetchServers = async () => {
    try {
      const serversData = await apiRequest<Server[]>('/servers');
      return serversData || [];
    } catch (error) {
      console.error('Error fetching servers:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load servers';
      message.error(t('streams.notifications.error', { message: errorMessage }));
      return [];
    }
  };

  // Fetch categories
  const fetchCategories = async () => {
    try {
      return await categoryService.getAllCategories();
    } catch (error) {
      console.error('Error fetching categories:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load categories';
      message.error(t('streams.notifications.error', { message: errorMessage }));
      return [];
    }
  };

  // Fetch deliveries
  const fetchDeliveries = async () => {
    try {
      return await deliveryService.getAllDeliveries();
    } catch (error) {
      console.error('Error fetching deliveries:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load deliveries';
      message.error(t('streams.notifications.error', { message: errorMessage }));
      return [];
    }
  };

  useEffect(() => {
    const fetchFormData = async () => {
      setLoading(true);
      try {
        // Fetch categories, servers, and deliveries in parallel
        const [categoriesData, serversData, deliveriesData] = await Promise.all([
          fetchCategories(),
          fetchServers(),
          fetchDeliveries()
        ]);

        setCategories(categoriesData);
        setServers(serversData);
        setDeliveries(deliveriesData);

        // Fetch incoming streams, advertisement sets, and logos
        await Promise.all([
          fetchIncomingStreams(),
          fetchAdvSets(),
          fetchLogos()
        ]);

        // If editing, fetch the stream data and blocked advertisement sets
        if (isEdit && id) {
          await fetchBlockedAdvSets();
          const streamData = await streamService.getStreamById(Number(id));
          setStreamData(streamData);
          
          // Reset image dimensions when loading new stream data
          setHorizontalImageDimensions(null);
          setVerticalImageDimensions(null);
          
          // Parse dates with explicit UTC handling to avoid timezone issues
          const parseDateTime = (dateStr: string) => {
            // Strip timezone information and parse as local time to avoid increment issues
            const cleanDateStr = dateStr.replace(/[TZ].*$/, '').replace(/\+.*$/, '');
            const match = cleanDateStr.match(/(\d{4})-(\d{2})-(\d{2})\s*(\d{2}):(\d{2}):(\d{2})/);
            if (match) {
              const [, year, month, day, hour, minute, second] = match;
              return dayjs()
                .year(parseInt(year, 10))
                .month(parseInt(month, 10) - 1) // dayjs months are 0-indexed
                .date(parseInt(day, 10))
                .hour(parseInt(hour, 10))
                .minute(parseInt(minute, 10))
                .second(parseInt(second, 10))
                .millisecond(0);
            }
            // Fallback to direct parsing if regex doesn't match
            return dayjs(dateStr);
          };

          form.setFieldsValue({
            ...streamData,
            start_datetime: parseDateTime(streamData.start_datetime),
            end_datetime: parseDateTime(streamData.end_datetime),
          });
        } else {
          // If creating a new stream, set the default delivery
          const defaultDelivery = deliveriesData.find(delivery => delivery.is_default);
          if (defaultDelivery) {
            form.setFieldsValue({
              delivery_id: defaultDelivery.id
            });
          }
        }
      } catch (error) {
        console.error('Error fetching form data:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to load form data';
        message.error(t('streams.notifications.error', { message: errorMessage }));
      } finally {
        setLoading(false);
      }
    };

    fetchFormData();
  }, [isEdit, id, form, t]);

  const onFinish = async (values: any) => {
    setSubmitting(true);
    setFormLocked(true);
    
    try {
      const streamRequestData: StreamRequest = {
        ...values,
        start_datetime: values.start_datetime.toISOString(),
        end_datetime: values.end_datetime.toISOString(),
        // Preserve existing image data when updating
        ...(isEdit && streamData && {
          image_horizontal: streamData.image_horizontal,
          image_vertical: streamData.image_vertical,
        }),
      };

      // Exclude logo_id from regular stream updates - it's managed separately in Logo Management
      if (isEdit) {
        delete streamRequestData.logo_id;
      }

      if (isEdit && id) {
        await streamService.updateStream(Number(id), streamRequestData);
        message.success(t('streams.notifications.update_success'));
        // Intentionally not navigating away after update - stay on edit form
      } else {
        await streamService.createStream(streamRequestData);
        message.success(t('streams.notifications.create_success'));
        // Only navigate to streams list after creating a new stream
        navigate('/streams');
      }
        
    } catch (error) {
      console.error('Error submitting form:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to save stream';
      message.error(t('streams.notifications.error', { message: errorMessage }));
    } finally {
      setSubmitting(false);
      setFormLocked(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    
    setDeleting(true);
    try {
      await streamService.deleteStream(Number(id));
      message.success(t('streams.notifications.delete_success'));
      navigate('/streams');
    } catch (error) {
      console.error('Error deleting stream:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete stream';
      message.error(t('streams.notifications.error', { message: errorMessage }));
    } finally {
      setDeleting(false);
    }
  };

  const handleCancel = () => {
    navigate('/streams');
  };

  // Handle incoming stream selection to update server field
  const handleIncomingStreamChange = (value: string | undefined) => {
    if (!value) {
      // If selection is cleared, reset the server field
      form.setFieldsValue({
        server_id: ''
      });
      return;
    }
    
    const selectedStream = incomingStreams.find(stream => stream.id === value);
    if (selectedStream) {
      form.setFieldsValue({
        server_id: selectedStream.server_id
      });
    }
  };

  const handleStartStream = async () => {
    if (!id) return;
    
    setProcessingStream(true);
    try {
      await streamService.startStream(Number(id));
      message.success(t('streams.notifications.start_success', 'Stream started successfully'));
      
      // Show spinner for 3 seconds then refresh
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    } catch (error) {
      console.error('Error starting stream:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to start stream';
      message.error(t('streams.notifications.error', { message: errorMessage }));
      setProcessingStream(false);
    }
  };
  
  const handleStopStream = async () => {
    if (!id) return;
    
    setProcessingStream(true);
    try {
      await streamService.stopStream(Number(id));
      message.success(t('streams.notifications.stop_success', 'Stream stopped successfully'));
      
      // Show spinner for 3 seconds then refresh
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    } catch (error) {
      console.error('Error stopping stream:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to stop stream';
      message.error(t('streams.notifications.error', { message: errorMessage }));
      setProcessingStream(false);
    }
  };

  // Navigation handlers for previous/next streams
  const handleNavigateToPrevious = () => {
    if (streamData?.neighbor_streams?.previous) {
      navigate(`/streams/${streamData.neighbor_streams.previous.id}/edit`);
    }
  };

  const handleNavigateToNext = () => {
    if (streamData?.neighbor_streams?.next) {
      navigate(`/streams/${streamData.neighbor_streams.next.id}/edit`);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', gap: '20px' }}>
        <Card title={isEdit ? t('streams.form.edit_title') : t('streams.form.create_title')} style={{ width: '50%' }}>
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
          </div>
        </Card>
        {isEdit && (
          <Card title={t('streams.form.stream_control', 'Stream Control')} style={{ width: '50%' }}>
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Spin size="large" />
            </div>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div>
      {/* Navigation buttons for edit mode */}
      {isEdit && streamData?.neighbor_streams && (
        <div style={{ 
          marginBottom: '20px', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          gap: '20px',
          flexWrap: 'wrap'
        }}>
          <Button
            type="default"
            icon={<LeftOutlined />}
            onClick={handleNavigateToNext}
            disabled={!streamData.neighbor_streams.next}
            style={{ 
              opacity: streamData.neighbor_streams.next ? 1 : 0.5,
              maxWidth: 'calc(50% - 10px)',
              minWidth: '200px',
              height: 'auto',
              whiteSpace: 'normal',
              textAlign: 'left',
              padding: '8px 16px'
            }}
          >
            <span style={{ 
              display: 'block',
              wordBreak: 'break-word',
              lineHeight: '1.4'
            }}>
              {streamData.neighbor_streams.next 
                ? t('streams.navigation.previous', 'Previous: {{title}}', { title: streamData.neighbor_streams.next.title })
                : t('streams.navigation.no_previous', 'No previous stream')
              }
            </span>
          </Button>
          
          <Button
            type="default"
            onClick={handleNavigateToPrevious}
            disabled={!streamData.neighbor_streams.previous}
            style={{ 
              opacity: streamData.neighbor_streams.previous ? 1 : 0.5,
              maxWidth: 'calc(50% - 10px)',
              minWidth: '200px',
              height: 'auto',
              whiteSpace: 'normal',
              textAlign: 'right',
              padding: '8px 16px'
            }}
          >
            <span style={{ 
              display: 'block',
              wordBreak: 'break-word',
              lineHeight: '1.4'
            }}>
              {streamData.neighbor_streams.previous 
                ? t('streams.navigation.next', 'Next: {{title}}', { title: streamData.neighbor_streams.previous.title })
                : t('streams.navigation.no_next', 'No next stream')
              }
            </span>
            <RightOutlined style={{ marginLeft: '8px' }} />
          </Button>
        </div>
      )}
      
      <div style={{ display: 'flex', gap: '20px' }}>
        <Card title={isEdit ? t('streams.form.edit_title') : t('streams.form.create_title')} style={{ width: '50%' }}>
        {formLocked && (
          <Alert
            message={t('streams.form.waiting_streams', 'Waiting incoming streams to be online')}
            type="info"
            showIcon
            icon={<LoadingOutlined spin />}
            style={{ marginBottom: '16px', fontSize: '14px', padding: '12px 16px' }}
            banner
          />
        )}
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            end_datetime_flexible: false,
            published: true,
            is_planned: true,
            is_live: false,
            is_ended: false,
            status: 'planned'
          }}
          disabled={formLocked || submitting}
        >
          <Form.Item
            name="title"
            label={t('streams.form.title_field')}
            rules={[{ required: true, message: t('streams.form.title_required') }]}
          >
            <Input placeholder={t('streams.form.title_placeholder')} />
          </Form.Item>

          <Form.Item
            name="category_id"
            label={t('streams.form.category')}
          >
            <Select placeholder={t('streams.form.category')} allowClear>
              {categories.map(category => (
                <Option key={category.id} value={category.id}>{category.name}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="server_incoming_stream_id"
            label={t('streams.form.server_incoming_stream')}
            rules={[{ required: true, message: t('streams.form.server_incoming_stream_required', 'Please select an incoming stream') }]}
          >
            <Select 
              placeholder={t('streams.form.server_incoming_stream_placeholder')}
              loading={loadingIncomingStreams}
              onChange={handleIncomingStreamChange}
            >
              {[...incomingStreams]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(stream => (
                <Option key={stream.id} value={stream.id}>{stream.name}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="start_datetime"
            label={t('streams.form.start_datetime')}
            rules={[{ required: true, message: t('streams.form.start_required') }]}
          >
            <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="end_datetime"
            label={t('streams.form.end_datetime')}
            rules={[
              { required: true, message: t('streams.form.end_required') },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || !getFieldValue('start_datetime')) {
                    return Promise.resolve();
                  }
                  if (value.isBefore(getFieldValue('start_datetime'))) {
                    return Promise.reject(new Error(t('streams.form.end_before_start_error', 'End date/time cannot be before start date/time')));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
            dependencies={['start_datetime']}
          >
            <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="end_datetime_flexible"
            label={t('streams.form.end_datetime_flexible')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="published"
            label={t('streams.form.published', 'Publish to API')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          {/* Hidden Server field */}
          <Form.Item
            name="server_id"
            hidden
            rules={[{ required: true, message: t('streams.form.server_required') }]}
          >
            <Input type="hidden" />
          </Form.Item>

          <Form.Item
            name="delivery_id"
            label={t('streams.form.delivery')}
            rules={[{ required: true, message: t('streams.form.delivery_required') }]}
          >
            <Select placeholder={t('streams.form.delivery')}>
              {[...deliveries]
                .sort((a, b) => (a.name || a.domain).localeCompare(b.name || b.domain))
                .map(delivery => (
                <Option key={delivery.id} value={delivery.id}>{delivery.name || delivery.domain}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label={t('streams.form.description')}
          >
            <TextArea rows={4} placeholder={t('streams.form.description_placeholder')} />
          </Form.Item>

          {/* Image Upload Fields - Only show when editing existing stream */}
          {isEdit && (
            <>
              {/* Image Horizontal Upload */}
              <Form.Item
                label={t('streams.form.image_horizontal', 'Image Horizontal')}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <Upload
                    name="image_horizontal"
                    accept=".png,.jpg,.jpeg"
                    showUploadList={false}
                    beforeUpload={(file) => {
                      handleImageUpload(file, 'horizontal');
                      return false; // Prevent default upload
                    }}
                    disabled={uploadingHorizontal || formLocked || submitting}
                  >
                    <Button 
                      icon={<UploadOutlined />} 
                      loading={uploadingHorizontal}
                      disabled={formLocked || submitting}
                    >
                      {t('streams.form.upload_horizontal', 'Upload Horizontal Image')}
                    </Button>
                  </Upload>
                  {streamData?.image_horizontal && streamData.image_horizontal !== null && streamData.image_horizontal !== "" && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <img 
                        src={`${streamData.image_horizontal}?t=${horizontalImageKey}`} 
                        alt="Horizontal preview" 
                        style={{ 
                          width: '60px', 
                          height: '40px', 
                          objectFit: 'cover', 
                          borderRadius: '4px',
                          cursor: 'pointer',
                          transition: 'opacity 0.2s'
                        }}
                        onClick={() => handleImagePreviewClick(streamData.image_horizontal!)}
                        onMouseEnter={(e) => (e.target as HTMLImageElement).style.opacity = '0.8'}
                        onMouseLeave={(e) => (e.target as HTMLImageElement).style.opacity = '1'}
                        onLoad={(e) => handleImageLoad('horizontal', e)}
                        title={t('streams.form.click_to_view_full', 'Click to view full size image')}
                      />
                      <span style={{ fontSize: '12px', color: '#666' }}>
                        {horizontalImageDimensions 
                          ? `${horizontalImageDimensions.width} x ${horizontalImageDimensions.height}`
                          : t('streams.form.current_image', 'Current image')
                        }
                      </span>
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        loading={uploadingHorizontal}
                        disabled={formLocked || submitting}
                        onClick={() => handleImageDelete('horizontal')}
                        title={t('streams.form.delete_image', 'Delete image')}
                      />
                    </div>
                  )}
                </div>
              </Form.Item>

              {/* Image Vertical Upload */}
              <Form.Item
                label={t('streams.form.image_vertical', 'Image Vertical')}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <Upload
                    name="image_vertical"
                    accept=".png,.jpg,.jpeg"
                    showUploadList={false}
                    beforeUpload={(file) => {
                      handleImageUpload(file, 'vertical');
                      return false; // Prevent default upload
                    }}
                    disabled={uploadingVertical || formLocked || submitting}
                  >
                    <Button 
                      icon={<UploadOutlined />} 
                      loading={uploadingVertical}
                      disabled={formLocked || submitting}
                    >
                      {t('streams.form.upload_vertical', 'Upload Vertical Image')}
                    </Button>
                  </Upload>
                  {streamData?.image_vertical && streamData.image_vertical !== null && streamData.image_vertical !== "" && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <img 
                        src={`${streamData.image_vertical}?t=${verticalImageKey}`} 
                        alt="Vertical preview" 
                        style={{ 
                          width: '40px', 
                          height: '60px', 
                          objectFit: 'cover', 
                          borderRadius: '4px',
                          cursor: 'pointer',
                          transition: 'opacity 0.2s'
                        }}
                        onClick={() => handleImagePreviewClick(streamData.image_vertical!)}
                        onMouseEnter={(e) => (e.target as HTMLImageElement).style.opacity = '0.8'}
                        onMouseLeave={(e) => (e.target as HTMLImageElement).style.opacity = '1'}
                        onLoad={(e) => handleImageLoad('vertical', e)}
                        title={t('streams.form.click_to_view_full', 'Click to view full size image')}
                      />
                      <span style={{ fontSize: '12px', color: '#666' }}>
                        {verticalImageDimensions 
                          ? `${verticalImageDimensions.width} x ${verticalImageDimensions.height}`
                          : t('streams.form.current_image', 'Current image')
                        }
                      </span>
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        loading={uploadingVertical}
                        disabled={formLocked || submitting}
                        onClick={() => handleImageDelete('vertical')}
                        title={t('streams.form.delete_image', 'Delete image')}
                      />
                    </div>
                  )}
                </div>
              </Form.Item>
            </>
          )}

          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {isEdit && (
                <Popconfirm
                  title={t('streams.actions.confirm_delete')}
                  onConfirm={handleDelete}
                  okText={t('streams.actions.confirm_yes', 'Yes')}
                  cancelText={t('streams.actions.confirm_no', 'No')}
                  okButtonProps={{ danger: true }}
                  disabled={formLocked}
                >
                  <Button 
                    danger 
                    type="primary" 
                    icon={<DeleteOutlined />}
                    loading={deleting}
                    disabled={formLocked}
                  >
                    {t('streams.actions.delete')}
                  </Button>
                </Popconfirm>
              )}
              <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px' }}>
                <Button onClick={handleCancel} disabled={formLocked || submitting}>
                  {t('streams.form.back_to_list')}
                </Button>
                <Button type="primary" htmlType="submit" loading={submitting} disabled={formLocked}>
                  {t('streams.form.submit')}
                </Button>
              </div>
            </div>
          </Form.Item>
        </Form>
      </Card>
      {isEdit && (
        <Card title={t('streams.form.stream_control', 'Stream Control')} style={{ width: '50%' }}>
          {processingStream ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Spin size="large" />
              <p style={{ marginTop: '20px' }}>{t('streams.form.processing_stream', 'Processing stream operation...')}</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <Space direction="horizontal" size="middle">
                  {streamData?.is_planned && (
                    <Button 
                      type="primary" 
                      icon={<PlayCircleOutlined />}
                      style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                      onClick={handleStartStream}
                      disabled={formLocked || submitting}
                    >
                      {t('streams.actions.start_now', 'Start Stream now')}
                    </Button>
                  )}
                  
                  {streamData?.is_live && (
                    <Button 
                      type="primary" 
                      danger 
                      icon={<StopOutlined />}
                      onClick={handleStopStream}
                      disabled={formLocked || submitting}
                    >
                      {t('streams.actions.stop_now', 'Stop Stream now')}
                    </Button>
                  )}
                </Space>
              </div>
              
              {streamData?.is_live && streamData?.server_id && (
                <>
                  <div style={{ marginTop: '20px' }}>
                    <Radio.Group 
                      value={activePlayer} 
                      onChange={(e) => setActivePlayer(e.target.value)}
                      buttonStyle="solid"
                      style={{ marginBottom: '20px' }}
                    >
                      <Radio.Button value="outgoing">{t('streams.player.outgoing_stream', 'Outgoing stream CDN')}</Radio.Button>
                      <Radio.Button value="origin">{t('streams.player.outgoing_stream_origin', 'Outgoing stream origin')}</Radio.Button>
                      <Radio.Button value="incoming">{t('streams.player.incoming_stream', 'Incoming stream')}</Radio.Button>
                    </Radio.Group>

                    {activePlayer === 'incoming' && (
                      <div key="incoming-player">
                        <Suspense fallback={<Spin />}>
                          <VideoPlayerContainer key="incoming" streamData={streamData} servers={servers} />
                        </Suspense>
                        
                        {/* Incoming Stream URL */}
                        <div style={{ marginTop: '20px' }}>
                          <h4 style={{ marginBottom: '16px' }}>Stream URL</h4>
                          <Input.Group compact>
                            <Input
                              value={generateIncomingStreamUrl()}
                              disabled
                              style={{ width: 'calc(100% - 80px)' }}
                              placeholder="Incoming stream URL"
                            />
                            <Button
                              icon={<CopyOutlined />}
                              disabled={!generateIncomingStreamUrl()}
                              onClick={() => copyToClipboard(
                                generateIncomingStreamUrl(), 
                                'Incoming stream URL'
                              )}
                              style={{ width: '40px' }}
                            />
                            <Button
                              icon={<PlaySquareOutlined />}
                              disabled={!generateIncomingStreamUrl()}
                              onClick={() => openStreamInPlayer(generateIncomingStreamUrl())}
                              style={{ width: '40px' }}
                              title="Open in player"
                            />
                          </Input.Group>
                        </div>
                      </div>
                    )}
                    
                    {activePlayer === 'outgoing' && (
                      <div key="outgoing-player">
                        <Suspense fallback={<Spin />}>
                          <OutgoingStreamPlayerContainer key="outgoing" streamData={streamData} deliveries={deliveries} />
                        </Suspense>
                        
                        {/* Stream Quality URLs */}
                        <div style={{ marginTop: '20px' }}>
                          <h4 style={{ marginBottom: '16px' }}>Stream URLs</h4>
                          {(() => {
                            const streamUrls = generateStreamUrls();
                            const qualities = [
                              { label: 'Auto', key: 'auto' },
                              { label: '1080', key: '1080' },
                              { label: '720', key: '720' },
                              { label: '480', key: '480' },
                              { label: '360', key: '360' }
                            ];
                            
                            return qualities.map(quality => (
                              <div key={quality.key} style={{ marginBottom: '12px' }}>
                                <div style={{ marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                                  {quality.label}
                                </div>
                                <Input.Group compact>
                                  <Input
                                    value={streamUrls[quality.key as keyof typeof streamUrls]}
                                    disabled
                                    style={{ width: 'calc(100% - 80px)' }}
                                    placeholder={`${quality.label} stream URL`}
                                  />
                                  <Button
                                    icon={<CopyOutlined />}
                                    disabled={!streamUrls[quality.key as keyof typeof streamUrls]}
                                    onClick={() => copyToClipboard(
                                      streamUrls[quality.key as keyof typeof streamUrls], 
                                      quality.label
                                    )}
                                    style={{ width: '40px' }}
                                  />
                                  <Button
                                    icon={<PlaySquareOutlined />}
                                    disabled={!streamUrls[quality.key as keyof typeof streamUrls]}
                                    onClick={() => openStreamInPlayer(streamUrls[quality.key as keyof typeof streamUrls])}
                                    style={{ width: '40px' }}
                                    title="Open in player"
                                  />
                                </Input.Group>
                              </div>
                            ));
                          })()}
                        </div>
                      </div>
                    )}

                    {activePlayer === 'origin' && (
                      <div key="origin-player">
                        <Suspense fallback={<Spin />}>
                          <VideoPlayerContainer key="origin" streamData={streamData} servers={servers} />
                        </Suspense>
                        
                        {/* Stream Quality URLs for Origin */}
                        <div style={{ marginTop: '20px' }}>
                          <h4 style={{ marginBottom: '16px' }}>Stream URLs (Origin)</h4>
                          {(() => {
                            const streamUrls = generateOriginStreamUrls();
                            const qualities = [
                              { label: 'Auto', key: 'auto' },
                              { label: '1080', key: '1080' },
                              { label: '720', key: '720' },
                              { label: '480', key: '480' },
                              { label: '360', key: '360' }
                            ];
                            
                            return qualities.map(quality => (
                              <div key={quality.key} style={{ marginBottom: '12px' }}>
                                <div style={{ marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                                  {quality.label}
                                </div>
                                <Input.Group compact>
                                  <Input
                                    value={streamUrls[quality.key as keyof typeof streamUrls]}
                                    disabled
                                    style={{ width: 'calc(100% - 80px)' }}
                                    placeholder={`${quality.label} stream URL`}
                                  />
                                  <Button
                                    icon={<CopyOutlined />}
                                    disabled={!streamUrls[quality.key as keyof typeof streamUrls]}
                                    onClick={() => copyToClipboard(
                                      streamUrls[quality.key as keyof typeof streamUrls], 
                                      quality.label
                                    )}
                                    style={{ width: '40px' }}
                                  />
                                  <Button
                                    icon={<PlaySquareOutlined />}
                                    disabled={!streamUrls[quality.key as keyof typeof streamUrls]}
                                    onClick={() => openStreamInPlayer(streamUrls[quality.key as keyof typeof streamUrls])}
                                    style={{ width: '40px' }}
                                    title="Open in player"
                                  />
                                </Input.Group>
                              </div>
                            ));
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
              
              {/* Advertisement Management Section */}
              {(streamData?.is_live || streamData?.is_planned) && (
                <div style={{ marginTop: '20px' }}>
                  <h4 style={{ marginBottom: '16px' }}>Advertisement Management</h4>
                  
                  {/* Active Advertisement Sets */}
                  {advSets.length > 0 ? (
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ marginBottom: '8px', fontSize: '12px', color: '#666' }}>
                        Active Advertisement Sets:
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {advSets.map(advSet => {
                          const isBlocked = blockedAdvSets.includes(advSet.id);
                          const isToggling = togglingAdvSet === advSet.id;
                          
                          return (
                            <div key={advSet.id} style={{ 
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              fontSize: '14px', 
                              padding: '8px 12px', 
                              backgroundColor: isBlocked ? '#ff4d4f' : '#1890ff', 
                              color: 'white',
                              borderRadius: '6px',
                              border: isBlocked ? '1px solid #cf1322' : '1px solid #0050b3',
                              fontWeight: '500',
                              opacity: isToggling ? 0.7 : 1
                            }}>
                              <span>{advSet.adv_set_name}</span>
                              <Switch
                                checked={!isBlocked}
                                onChange={() => handleToggleAdvSet(advSet.id)}
                                loading={isToggling}
                                size="small"
                                style={{
                                  backgroundColor: isBlocked ? '#ff7875' : '#52c41a'
                                }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div style={{ 
                      marginBottom: '16px', 
                      padding: '12px', 
                      backgroundColor: '#fff7e6', 
                      border: '1px solid #ffd591',
                      borderRadius: '4px',
                      fontSize: '13px',
                      color: '#d48806'
                    }}>
                      No active advertisement sets available
                    </div>
                  )}
                  
                  {/* Start Now Button - Only show for live streams */}
                  {streamData?.is_live && (
                    <Button 
                      type="primary"
                      style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
                      onClick={handleTriggerAd}
                      disabled={triggeringAd || adCooldown > 0 || startNowLockTimer > 0 || advSets.length === 0}
                      loading={triggeringAd}
                    >
                      {startNowLockTimer > 0 
                        ? `Start now (${startNowLockTimer}s)` 
                        : adCooldown > 0 
                        ? `Start now (${adCooldown}s)` 
                        : 'Start now'
                      }
                    </Button>
                  )}
                </div>
              )}

              {/* Logo Management Section */}
              {isEdit && streamData && !streamData.is_ended && (
                <div style={{ marginTop: '20px' }}>
                  <h4 style={{ marginBottom: '16px' }}>Logo Management</h4>
                  
                  {logos.length > 0 ? (
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ marginBottom: '8px', fontSize: '12px', color: '#666' }}>
                        Available Logos:
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {/* Logo Options */}
                        {logos.map(logo => {
                          const isSelected = streamData.logo_id === logo.id;
                          
                          return (
                            <div key={logo.id} style={{ 
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              fontSize: '14px', 
                              padding: '8px 12px', 
                              backgroundColor: isSelected ? '#1890ff' : '#f5f5f5', 
                              color: isSelected ? 'white' : '#666',
                              borderRadius: '6px',
                              border: isSelected ? '1px solid #0050b3' : '1px solid #d9d9d9',
                              fontWeight: '500',
                              opacity: updatingLogo ? 0.7 : 1
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span>{logo.logo_name}</span>
                                {logo.is_default && (
                                  <span style={{ 
                                    fontSize: '10px', 
                                    padding: '2px 6px',
                                    backgroundColor: isSelected ? 'rgba(255,255,255,0.2)' : '#faad14',
                                    color: isSelected ? 'white' : 'white',
                                    borderRadius: '3px',
                                    fontWeight: 'bold'
                                  }}>
                                    DEFAULT
                                  </span>
                                )}
                              </div>
                              <Switch
                                checked={isSelected}
                                onChange={(checked) => {
                                  // Prevent turning off the currently selected logo
                                  // Only allow switching to a different logo
                                  if (checked || !isSelected) {
                                    handleLogoChange(checked ? logo.id : null);
                                  }
                                }}
                                loading={updatingLogo}
                                size="small"
                                disabled={streamData.is_live}
                                style={{
                                  backgroundColor: isSelected ? '#52c41a' : '#d9d9d9'
                                }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div style={{ 
                      marginBottom: '16px', 
                      padding: '12px', 
                      backgroundColor: '#fff7e6', 
                      border: '1px solid #ffd591',
                      borderRadius: '4px',
                      fontSize: '13px',
                      color: '#d48806'
                    }}>
                      No logos available. Create logos in the Logo Management section.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </Card>
      )}
      </div>
    </div>
  );
};

export default StreamForm; 